import { DollarSign, Users, TrendingUp, Activity, AlertTriangle } from 'lucide-react';
import { founderAnalyticsService } from '../../lib/founder-analytics-service';
import { comparisonUtils } from '../../lib/comparison-utils';

interface MetricCardProps {
  title: string;
  value: string | number;
  comparison?: {
    current: number;
    previous: number;
  };
  icon: React.ElementType;
  higherIsBetter?: boolean;
}

function MetricCard({ title, value, comparison, icon: Icon, higherIsBetter = true }: MetricCardProps) {
  const comparisonResult = comparison
    ? comparisonUtils.compare(comparison.current, comparison.previous, higherIsBetter)
    : null;

  return (
    <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-[#6B7280]">{title}</h3>
        <div className="p-2 bg-[#E8F2F0] rounded-[8px]">
          <Icon className="text-[#8CAFA8]" size={20} />
        </div>
      </div>
      <div className="text-3xl font-bold text-[#1F2937] mb-2">{value}</div>
      {comparisonResult && (
        <div className={`flex items-center gap-1 text-sm ${comparisonUtils.getTrendColor(comparisonResult.isPositive)}`}>
          <span>{comparisonUtils.getTrendIcon(comparisonResult.trend)}</span>
          <span>{comparisonUtils.formatChangePercent(comparisonResult)}</span>
          <span className="text-[#6B7280] ml-1">vs last week</span>
        </div>
      )}
    </div>
  );
}

export function OverviewTab({ data, onRefresh }: { data: any; onRefresh: () => void }) {
  if (!data) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 border-4 border-[#8CAFA8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#6B7280]">Loading overview...</p>
      </div>
    );
  }

  const { mrr, userGrowth, engagement, churn, feedback, aiUsage, activity, alerts } = data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Monthly Recurring Revenue"
          value={founderAnalyticsService.formatCurrency(mrr?.current_mrr || 0)}
          comparison={mrr ? {
            current: mrr.current_mrr,
            previous: mrr.last_week_mrr || 0
          } : undefined}
          icon={DollarSign}
        />

        <MetricCard
          title="Total Users"
          value={founderAnalyticsService.formatNumber(userGrowth?.total_users || 0)}
          comparison={userGrowth ? {
            current: userGrowth.this_week_signups,
            previous: userGrowth.last_week_signups
          } : undefined}
          icon={Users}
        />

        <MetricCard
          title="Daily Active Users"
          value={founderAnalyticsService.formatNumber(engagement?.dau_today || 0)}
          comparison={engagement ? {
            current: engagement.dau_today,
            previous: engagement.dau_yesterday
          } : undefined}
          icon={Activity}
        />

        <MetricCard
          title="Churn Rate (30d)"
          value={`${churn?.churn_rate_30d || 0}%`}
          comparison={undefined}
          icon={AlertTriangle}
          higherIsBetter={false}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4">User Growth</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-[#6B7280]">Today</span>
              <span className="font-semibold text-[#1F2937]">{userGrowth?.today_signups || 0} signups</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#6B7280]">This Week</span>
              <span className="font-semibold text-[#1F2937]">{userGrowth?.this_week_signups || 0} signups</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#6B7280]">This Month</span>
              <span className="font-semibold text-[#1F2937]">{userGrowth?.this_month_signups || 0} signups</span>
            </div>
            {userGrowth && (
              <div className="pt-3 border-t border-gray-100">
                <div className={`text-sm ${comparisonUtils.getTrendColor(
                  comparisonUtils.getWeekOverWeekComparison(
                    userGrowth.this_week_signups,
                    userGrowth.last_week_signups
                  ).isPositive
                )}`}>
                  {comparisonUtils.formatChangePercent(
                    comparisonUtils.getWeekOverWeekComparison(
                      userGrowth.this_week_signups,
                      userGrowth.last_week_signups
                    )
                  )} vs last week
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4">Feedback Activity</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-[#6B7280]">Total Requests</span>
              <span className="font-semibold text-[#1F2937]">{feedback?.total_requests || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#6B7280]">Total Responses</span>
              <span className="font-semibold text-[#1F2937]">{feedback?.total_responses || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#6B7280]">Audio Responses</span>
              <span className="font-semibold text-[#1F2937]">{feedback?.total_audio_responses || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#6B7280]">Response Rate</span>
              <span className="font-semibold text-[#1F2937]">{feedback?.overall_response_rate?.toFixed(1) || 0}%</span>
            </div>
          </div>
        </div>
      </div>

      {alerts && alerts.length > 0 && (
        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4">Recent Alerts</h3>
          <div className="space-y-3">
            {alerts.slice(0, 5).map((alert: any) => (
              <div
                key={alert.id}
                className={`p-4 rounded-[12px] border ${
                  alert.severity === 'critical'
                    ? 'bg-red-50 border-red-200'
                    : alert.severity === 'warning'
                    ? 'bg-orange-50 border-orange-200'
                    : 'bg-[#E8F2F0] border-[#8CAFA8]/30'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-[#1F2937]">{alert.title}</h4>
                    <p className="text-sm text-[#6B7280] mt-1">{alert.message}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    alert.severity === 'critical'
                      ? 'bg-red-100 text-red-700'
                      : alert.severity === 'warning'
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-[#8CAFA8]/20 text-[#1F2937]'
                  }`}>
                    {alert.severity}
                  </span>
                </div>
                <div className="text-xs text-[#6B7280] mt-2">
                  {new Date(alert.sent_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activity && activity.length > 0 && (
        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {activity.slice(0, 10).map((item: any, index: number) => (
              <div key={index} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#1F2937] capitalize">
                      {item.event_type.replace('_', ' ')}
                    </span>
                    <span className="text-xs text-[#6B7280]">
                      {new Date(item.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm text-[#6B7280] mt-1">
                    {item.event_type === 'signup' && item.event_data.email}
                    {item.event_type === 'subscription' && `${item.event_data.plan_type} - ${item.event_data.status}`}
                    {item.event_type === 'feedback_request' && item.event_data.recipient_email}
                    {item.event_type === 'feedback_response' && (item.event_data.has_audio ? 'Audio response' : 'Text response')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
